import React, { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import '../admin/layout/adminLayout.css'

type AdminNavItem = {
  label: string
  to: string
  icon?: string
}

const AdminLayout: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false)
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)')

    const syncFromMedia = (matches: boolean) => {
      setIsMobile(matches)
      setIsOpen(!matches)
    }

    syncFromMedia(media.matches)

    const handler = (event: MediaQueryListEvent) => {
      syncFromMedia(event.matches)
    }

    if (media.addEventListener) {
      media.addEventListener('change', handler)
    } else {
      media.addListener(handler)
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handler)
      } else {
        media.removeListener(handler)
      }
    }
  }, [])

  const navItems = useMemo<AdminNavItem[]>(
    () => [
      { label: 'Dashboard', to: '/admin', icon: '📊' },
      { label: 'Commandes', to: '/admin/orders', icon: '🧾' },
      { label: 'Services & options', to: '/admin/services', icon: '🧵' },
      { label: 'Prises en charge & livraisons', to: '/admin/transport', icon: '🚚' },
      { label: 'Créneaux de livraison', to: '/admin/slots', icon: '🗓️' },
      { label: 'Comptabilité', to: '/admin/accounting', icon: '💸' },
      { label: 'Partenaires', to: '/admin/partners', icon: '🤝' },
      { label: 'Coupons', to: '/admin/coupons', icon: '🎟️' },
      { label: 'Comptes', to: '/admin/accounts', icon: '👥' },
      { label: 'Avis clients', to: '/admin/feedbacks', icon: '⭐' }
    ],
    []
  )

  const handleToggle = () => {
    setIsOpen(prev => !prev)
  }

  const handleNavClick = () => {
    if (isMobile) {
      setIsOpen(false)
    }
  }

  const shellClassName = [
    'admin-shell',
    isMobile ? 'is-mobile' : 'is-desktop',
    isOpen ? 'is-open' : 'is-collapsed'
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={shellClassName}>
      {isMobile && isOpen && <div className="admin-overlay" onClick={() => setIsOpen(false)} />}

      <aside className="admin-sidebar" data-testid="admin-sidebar">
        <div className="admin-sidebar-top">
          <div className="admin-brand">
            <span className="admin-brand-icon">🛠️</span>
            <span className="admin-brand-text">Backoffice</span>
          </div>
        </div>

        <nav className="admin-nav">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin'}
              onClick={handleNavClick}
              title={item.label}
              className={({ isActive }) =>
                ['admin-nav-link', isActive ? 'is-active' : ''].filter(Boolean).join(' ')
              }
            >
              <span className="admin-nav-icon" aria-hidden="true">
                {item.icon}
              </span>
              <span className="admin-nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar-bottom">
          <NavLink
            className="admin-nav-link"
            to="/"
            title="Retour au site public"
            onClick={handleNavClick}
          >
            <span className="admin-nav-icon" aria-hidden="true">
              ↩️
            </span>
            <span className="admin-nav-label">Retour au site</span>
          </NavLink>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-header">
          <button
            type="button"
            className="admin-toggle"
            data-testid="admin-toggle"
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Replier le menu' : 'Ouvrir le menu'}
            onClick={handleToggle}
          >
            ☰
          </button>
          <div className="admin-header-title">
            <span>Administration</span>
          </div>
          <NavLink className="admin-header-link" to="/">
            Retour au site
          </NavLink>
        </header>

        <div className="admin-content" data-testid="admin-content">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

export default AdminLayout
